import countyData from '../data/counties.json';

export interface FeeStructure {
  type: 'county' | 'state' | 'none';
  state?: string;
  county?: string;
  message: string;
  fees?: {
    titleFee: number;
    registrationFee: number;
    salesTax: number;
    otherFees: number;
  };
}

export interface QuoteRequest {
  state: string;
  county: string;
  vehicleInfo: {
    modelYear: string;
    make: string;
    bodyStyle: string;
    weight: string;
    fuelType: string;
  };
  transactionInfo: {
    purchasePrice: number;
    tradeInValue: number;
    isCommercial: boolean;
    isMilitary: boolean;
  };
}

export interface QuoteResponse {
  success: boolean;
  message: string;
  feeStructure: FeeStructure;
  totalFees: number;
  breakdown: {
    titleFee: number;
    registrationFee: number;
    salesTax: number;
    otherFees: number;
  };
}

class QuoteService {
  /**
   * Determine the appropriate fee structure based on state and county
   */
  determineFeeStructure(state: string, county: string): FeeStructure {
    if (!state || !county) {
      return {
        type: 'none',
        message: 'Please select both state and county',
      };
    }

    // Check if county exists in our data
    const stateCounties = countyData[state as keyof typeof countyData];
    if (!stateCounties || !stateCounties.includes(county)) {
      return {
        type: 'state',
        state,
        county,
        message: `County "${county}" not found in ${state}. Using state-level fees.`,
      };
    }

    // Check if county-specific fees exist (this would typically check a database)
    const hasCountyFees = this.checkCountyFees(state, county);

    if (hasCountyFees) {
      return {
        type: 'county',
        state,
        county,
        message: `Using county-specific fees for ${county} County, ${state}`,
        fees: this.getCountyFees(state, county),
      };
    } else {
      return {
        type: 'state',
        state,
        county,
        message: `No county-specific fees found. Using state-level fees for ${state}`,
        fees: this.getStateFees(state),
      };
    }
  }

  /**
   * Check if county-specific fees exist
   * This would typically query a database or API
   */
  private checkCountyFees(state: string, county: string): boolean {
    // For demonstration, we'll say certain counties have specific fees
    const countiesWithFees: Record<string, string[]> = {
      NC: ['New Hanover', 'Mecklenburg', 'Wake', 'Guilford'],
      SC: ['Charleston', 'Greenville', 'Richland'],
      GA: ['Fulton', 'DeKalb', 'Cobb', 'Gwinnett'],
    };

    return countiesWithFees[state]?.includes(county) || false;
  }

  /**
   * Get county-specific fees
   * This would typically come from a database or API
   */
  private getCountyFees(state: string, county: string) {
    // Example county-specific fees
    const countyFeeData: Record<string, Record<string, any>> = {
      NC: {
        'New Hanover': {
          titleFee: 56.0,
          registrationFee: 38.75,
          salesTax: 0.07,
          otherFees: 15.0,
        },
        Mecklenburg: {
          titleFee: 56.0,
          registrationFee: 38.75,
          salesTax: 0.0725,
          otherFees: 20.0,
        },
      },
      SC: {
        Charleston: {
          titleFee: 15.0,
          registrationFee: 40.0,
          salesTax: 0.09,
          otherFees: 10.0,
        },
      },
    };

    return countyFeeData[state]?.[county] || this.getStateFees(state);
  }

  /**
   * Get state-level fees
   * This would typically come from a database or API
   */
  private getStateFees(state: string) {
    const stateFeeData: Record<string, any> = {
      NC: {
        titleFee: 56.0,
        registrationFee: 38.75,
        salesTax: 0.03,
        otherFees: 10.0,
      },
      SC: {
        titleFee: 15.0,
        registrationFee: 40.0,
        salesTax: 0.06,
        otherFees: 5.0,
      },
      GA: {
        titleFee: 18.0,
        registrationFee: 20.0,
        salesTax: 0.04,
        otherFees: 8.0,
      },
    };

    return (
      stateFeeData[state] || {
        titleFee: 50.0,
        registrationFee: 30.0,
        salesTax: 0.05,
        otherFees: 10.0,
      }
    );
  }

  /**
   * Generate a complete quote
   */
  async generateQuote(request: QuoteRequest): Promise<QuoteResponse> {
    try {
      const feeStructure = this.determineFeeStructure(request.state, request.county);

      if (feeStructure.type === 'none') {
        return {
          success: false,
          message: feeStructure.message,
          feeStructure,
          totalFees: 0,
          breakdown: {
            titleFee: 0,
            registrationFee: 0,
            salesTax: 0,
            otherFees: 0,
          },
        };
      }

      const fees = feeStructure.fees!;
      const taxableAmount = request.transactionInfo.purchasePrice - request.transactionInfo.tradeInValue;

      const breakdown = {
        titleFee: fees.titleFee,
        registrationFee: fees.registrationFee,
        salesTax: taxableAmount * fees.salesTax,
        otherFees: fees.otherFees,
      };

      const totalFees = breakdown.titleFee + breakdown.registrationFee + breakdown.salesTax + breakdown.otherFees;

      return {
        success: true,
        message: `Quote generated successfully. ${feeStructure.message}`,
        feeStructure,
        totalFees,
        breakdown,
      };
    } catch (error) {
      return {
        success: false,
        message: 'Failed to generate quote. Please try again.',
        feeStructure: {
          type: 'none',
          message: 'Error occurred during quote generation',
        },
        totalFees: 0,
        breakdown: {
          titleFee: 0,
          registrationFee: 0,
          salesTax: 0,
          otherFees: 0,
        },
      };
    }
  }

  /**
   * Get available counties for a state
   */
  getCountiesForState(state: string): string[] {
    return countyData[state as keyof typeof countyData] || [];
  }

  /**
   * Get all available states
   */
  getAvailableStates(): string[] {
    return Object.keys(countyData);
  }
}

const quoteService = new QuoteService();
export default quoteService;
